import { Loader, ImageOff } from 'lucide-react';
import { useBuildProjectMediaUrl } from '../../hooks/useBuildProjectMediaUrl';
import type { BuildProjectMedia } from '../../types/buildTeam';
import { cn } from '../../utils/cn';

export interface BuildProjectMediaThumbnailProps {
  media: BuildProjectMedia;
  className?: string;
  imgClassName?: string;
  onClick?: () => void;
}

const FRAME_CLASS = 'rounded-lg overflow-hidden border border-glass-200/15 bg-glass-100/5';

export function BuildProjectMediaThumbnail({
  media,
  className,
  imgClassName,
  onClick,
}: BuildProjectMediaThumbnailProps) {
  const { url, loading, error } = useBuildProjectMediaUrl(media.id);

  const inner = loading ? (
    <div className="flex items-center justify-center w-full h-full min-h-[80px] bg-glass-100/5">
      <Loader className="w-5 h-5 animate-spin text-glass-400" />
    </div>
  ) : error || !url ? (
    <div className="flex flex-col items-center justify-center gap-1 w-full h-full min-h-[80px] bg-glass-100/5 text-glass-500 text-xs px-2 text-center">
      <ImageOff className="w-5 h-5" />
      {error ? 'Failed to load' : 'No preview'}
    </div>
  ) : (
    <img
      src={url}
      alt={media.caption || media.filename}
      className={cn('w-full h-full object-cover', imgClassName)}
      loading="lazy"
    />
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          FRAME_CLASS,
          'hover:border-glass-200/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/50',
          className
        )}
      >
        {inner}
      </button>
    );
  }

  return (
    <div className={cn(FRAME_CLASS, className)}>
      {inner}
    </div>
  );
}
